import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { CheckCircle2, Crown, LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CONTACT_EMAIL, PRIVACY_PATH, REGULATION_PATH } from "@/content/siteContent";
import { usePageSEO } from "@/hooks/usePageSEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getQueryValue = (name) => new URLSearchParams(window.location.search).get(name);

export default function PaymentSuccessPage() {
  usePageSEO({
    title: "Conferma pagamento",
    description: "Pagina di conferma del pagamento ticket Fanta Royal. Stato ordine e verifica finale del checkout Stripe.",
    canonicalPath: "/success",
    noindex: true,
  });

  const sessionId = useMemo(() => getQueryValue("session_id"), []);
  const [state, setState] = useState({
    status: sessionId ? "loading" : "error",
    message: sessionId
      ? "Stiamo verificando il tuo pagamento con Stripe."
      : "Sessione di pagamento non trovata. Torna al sito e riprova.",
    amount: null,
    currency: "EUR",
  });

  useEffect(() => {
    if (!sessionId) return undefined;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 10;

    const pollStatus = async () => {
      if (cancelled) return;
      attempts += 1;

      try {
        const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
        const data = response.data;

        if (cancelled) return;

        if (data.status === "paid") {
          setState({
            status: "paid",
            message: "Pagamento confermato. Il tuo ticket Fanta Royal è stato registrato con successo.",
            amount: data.amount_total,
            currency: data.currency,
          });
          return;
        }

        if (data.status === "expired" || data.status === "cancelled") {
          setState({
            status: data.status,
            message: data.message || "La sessione di pagamento non è più valida. Puoi riprovare quando vuoi.",
            amount: data.amount_total,
            currency: data.currency,
          });
          return;
        }

        setState({
          status: "processing",
          message: data.message || "Pagamento in verifica. Attendiamo la conferma finale di Stripe.",
          amount: data.amount_total,
          currency: data.currency,
        });

        if (attempts < maxAttempts) {
          window.setTimeout(pollStatus, 2500);
        } else {
          setState({
            status: "timeout",
            message:
              "Abbiamo ricevuto il ritorno dal checkout, ma la conferma finale richiede più tempo del previsto. Controlla presto o contattaci se necessario.",
            amount: data.amount_total,
            currency: data.currency,
          });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            status: "error",
            message: error.response?.data?.detail || "Non siamo riusciti a verificare lo stato del pagamento.",
            amount: null,
            currency: "EUR",
          });
        }
      }
    };

    pollStatus();

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="status-page-shell page-noise">
      <div className="status-page-inner">
        <Link to="/" className="brand-lockup" data-testid="success-page-home-link">
          <span className="brand-mark" aria-hidden="true">
            <Crown className="h-4 w-4" />
          </span>
          <div>
            <span className="wordmark">Fanta Royal</span>
            <p className="brand-tagline">Conquista la corona</p>
          </div>
        </Link>

        <Card className="luxury-card status-card" data-testid="payment-success-card">
          <CardContent className="p-6 sm:p-8">
            {state.status === "paid" ? (
              <div className="status-icon success">
                <CheckCircle2 className="h-8 w-8" />
              </div>
            ) : (
              <div className="status-icon pending">
                <LoaderCircle className="h-8 w-8 animate-spin" />
              </div>
            )}

            <span className="section-kicker">Pagamento ticket</span>
            <h1 className="section-title status-title">
              {state.status === "paid" ? "Il tuo ticket è confermato" : "Stiamo verificando il pagamento"}
            </h1>
            <p className="section-description status-copy" data-testid="payment-success-status-message">
              {state.message}
            </p>

            {state.amount ? (
              <div className="status-highlight" data-testid="payment-success-amount">
                <span>Importo verificato</span>
                <strong>
                  {state.amount} {state.currency}
                </strong>
              </div>
            ) : null}

            {sessionId ? (
              <p className="status-session-id" data-testid="payment-success-session-id">Sessione Stripe: {sessionId}</p>
            ) : null}

            <div className="status-actions">
              <Button asChild className="primary-cta">
                <Link to="/#ticket" data-testid="payment-success-primary-cta">Torna al ticket</Link>
              </Button>
              <Button asChild variant="outline" className="secondary-cta">
                <Link to={REGULATION_PATH} data-testid="payment-success-regulation-link">Regolamento</Link>
              </Button>
            </div>

            <div className="status-utility-links">
              <Link to={PRIVACY_PATH} className="footer-link" data-testid="payment-success-privacy-link">
                Privacy Policy
              </Link>
              <a href={`mailto:${CONTACT_EMAIL}`} className="footer-link" data-testid="payment-success-contact-link">
                {CONTACT_EMAIL}
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
